import { useEffect } from 'react';
import { UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import {
    DatePicker,
    DatePickerCalendar,
    DatePickerInput,
    DatePickerPopover,
    Typography,
} from '@vp/swan';
import { format } from 'date-fns';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { FormInput } from '../../BriefForm';
import { DateFieldInputFragment } from './DateField.generated';
import { useUpdateDateField } from './useUpdateDateField';

export type DateFieldProps = DateFieldInputFragment & {
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
    clearErrors: UseFormClearErrors<FormInput>;
};

export function Input({
    id,
    register,
    dateValue,
    setValue,
    required,
    clearErrors,
    errorMessage,
}: DateFieldProps) {
    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateDateField(id, briefId, dateValue);

    const errorMsg = errorMessage || __('This field is required');

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    useEffect(() => {
        setValue(id, formatDate(dateValue) ?? '');
    }, [dateValue, id, setValue]);

    return (
        <DatePicker
            selectionMode="single"
            selectedDate={dateValue ? new Date(dateValue) : null}
            onSelectedDateChange={(d) => {
                if (!d) return;

                setValue(id, d);
                updateMutation(formatDate(d));
                clearErrors(id);
            }}
            {...register(id, {
                required: required ? errorMsg : false,
            })}
            minDate={today}
            aria-required={required || undefined}
        >
            <DatePickerInput />
            <DatePickerPopover>
                <DatePickerCalendar />
            </DatePickerPopover>
        </DatePicker>
    );
}

interface DateAreaFieldViewProps {
    dateValue: DateFieldInputFragment['dateValue'];
}

export function View({ dateValue }: DateAreaFieldViewProps) {
    const { locale } = usePage();

    if (!dateValue) {
        return null;
    }

    return (
        <Typography style={{ whiteSpace: 'pre-wrap' }}>
            {formatViewDate(dateValue, locale)}
        </Typography>
    );
}

const DATE_FORMAT = 'yyyy-MM-dd';

function formatDate(date: Date | null) {
    if (!date) {
        return '';
    }
    return format(date, DATE_FORMAT);
}

function formatViewDate(dateString: string, locale: string) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale).format(date);
}
